import { useState } from "react";
import api from "../../utils/axiosInstance";
import { useToast } from "../Toast/ToastProvider";
import ConfirmModal from "../Modals/ConfirmModal";

const BULK_ACTIONS = {
  offer: { status: "Offered to Vendor", successVerb: "offered to vendor" },
  start: { status: "Started", successVerb: "started" },
  accept: { status: "Offer Accepted", successVerb: "marked as accepted" },
  cancel: { status: "Cancelled", successVerb: "cancelled" },
  draft: { status: "Draft", successVerb: "moved to draft" },
};

const pluralize = (n) => (n === 1 ? "" : "s");

/**
 * Shared state + handlers for the jobs bulk-action toolbar and the row
 * delete icon. Both the master Jobs page and the per-project Jobs tab use
 * this so the behavior (start precheck, toast feedback, confirm modals) is
 * identical across the app.
 *
 * @param {Array} options.jobs Current jobs array (used for the Start precheck).
 * @param {Function} options.refresh Async function to reload jobs after an action.
 *
 * Returns:
 * selectedJobs / setSelectedJobs - selection state, passed to the table.
 * handleBulkAction(action) - wire to the toolbar buttons.
 * handleDeleteJob(jobId) - wire to the row trash icon (opens the modal).
 * Modals - JSX node to render once at the page root.
 */
export const useJobActions = ({ jobs, refresh }) => {
  const toast = useToast();
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [jobToDeleteId, setJobToDeleteId] = useState(null);

  const reportOutcomes = (outcomes, { successVerb, failVerb }) => {
    const succeeded = outcomes.filter((o) => o.ok);
    const failed = outcomes.filter((o) => !o.ok);

    if (succeeded.length > 0) {
      toast.success(
        `${succeeded.length} job${pluralize(succeeded.length)} ${successVerb}.`
      );
    }
    if (failed.length > 0) {
      const codes = failed.map((f) => `J${f.jobId}`).join(", ");
      toast.error(`Could not ${failVerb} ${codes}: ${failed[0].message}`);
    }
  };

  const performBulkDelete = async () => {
    const ids = [...selectedJobs];
    setBulkDeleteOpen(false);

    const outcomes = await Promise.all(
      ids.map(async (jobId) => {
        try {
          await api.delete(`/jobs/${jobId}`, { withCredentials: true });
          return { jobId, ok: true };
        } catch (err) {
          return {
            jobId,
            ok: false,
            message:
              err.response?.data?.message || err.message || "Failed to delete",
          };
        }
      })
    );

    reportOutcomes(outcomes, { successVerb: "deleted", failVerb: "delete" });
    setSelectedJobs([]);
    await refresh();
  };

  const performSingleDelete = async () => {
    const jobId = jobToDeleteId;
    setJobToDeleteId(null);
    if (!jobId) return;

    try {
      await api.delete(`/jobs/${jobId}`, { withCredentials: true });
      toast.success("Job deleted.");
      await refresh();
    } catch (err) {
      console.error("Failed to delete job:", err);
      toast.error(err.response?.data?.message || "Failed to delete job.");
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedJobs.length === 0) {
      toast.info("Select at least one job first.");
      return;
    }

    if (action === "delete") {
      setBulkDeleteOpen(true);
      return;
    }

    const config = BULK_ACTIONS[action];
    if (!config) return;

    let targetIds = [...selectedJobs];

    // Start requires the vendor to have accepted (Offer Accepted) or the job
    // to be in Hold. Surface a friendly message for anything else and proceed
    // only with the eligible ones.
    if (action === "start") {
      const startable = ["Offer Accepted", "Hold"];
      const selected = jobs.filter((j) => targetIds.includes(j.id));
      const blocked = selected.filter((j) => !startable.includes(j.status));

      if (blocked.length > 0) {
        const codes = blocked.map((j) => `J${j.id}`).join(", ");
        toast.info(`Vendor has not accepted yet for ${codes}.`);
        targetIds = selected
          .filter((j) => startable.includes(j.status))
          .map((j) => j.id);
        if (targetIds.length === 0) return;
      }
    }

    const outcomes = await Promise.all(
      targetIds.map(async (jobId) => {
        try {
          await api.post(
            `/jobs/${jobId}/status`,
            { new_status: config.status, changed_by: "admin" },
            { withCredentials: true }
          );
          return { jobId, ok: true };
        } catch (err) {
          return {
            jobId,
            ok: false,
            message:
              err.response?.data?.message ||
              err.message ||
              "Failed to update status",
          };
        }
      })
    );

    reportOutcomes(outcomes, {
      successVerb: config.successVerb,
      failVerb: `update ${action === "draft" ? "to draft" : action}`,
    });

    setSelectedJobs([]);
    await refresh();
  };

  const handleDeleteJob = (jobId) => setJobToDeleteId(jobId);

  const Modals = (
    <>
      {bulkDeleteOpen && (
        <ConfirmModal
          title="Delete jobs"
          message={`Delete ${selectedJobs.length} job${pluralize(
            selectedJobs.length
          )}? This cannot be undone.`}
          confirmText="Delete"
          onCancel={() => setBulkDeleteOpen(false)}
          onConfirm={performBulkDelete}
        />
      )}
      {jobToDeleteId !== null && (
        <ConfirmModal
          title="Delete job"
          message="Delete this job? This cannot be undone."
          confirmText="Delete"
          onCancel={() => setJobToDeleteId(null)}
          onConfirm={performSingleDelete}
        />
      )}
    </>
  );

  return {
    selectedJobs,
    setSelectedJobs,
    handleBulkAction,
    handleDeleteJob,
    Modals,
  };
};
